import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import {
  GRUPO_OPTIONS,
  GRUPO_LABELS,
  STATUS_LABELS,
  formatDate,
} from "@/lib/constants";
import {
  Plus,
  MagnifyingGlass,
  X,
  Trash,
  UploadSimple,
  FileArrowDown,
  Warning,
  ArrowsClockwise,
  Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LoadError from "@/components/LoadError";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

const SORT_OPTIONS = [
  { value: "numero_asc", label: "Nº brinco (crescente)" },
  { value: "numero_desc", label: "Nº brinco (decrescente)" },
  { value: "nome_asc", label: "Nome (A-Z)" },
  { value: "nome_desc", label: "Nome (Z-A)" },
  { value: "nascimento_desc", label: "Nascimento (mais novos)" },
  { value: "nascimento_asc", label: "Nascimento (mais velhos)" },
  { value: "del_desc", label: "DEL (maior)" },
  { value: "del_asc", label: "DEL (menor)" },
  { value: "producao_desc", label: "Produção 7d (maior)" },
  { value: "producao_asc", label: "Produção 7d (menor)" },
  { value: "gmd_desc", label: "GMD (maior)" },
  { value: "gmd_asc", label: "GMD (menor)" },
];

function AnimalForm({ onClose, onSaved, mothers }) {
  const [f, setF] = useState({
    numero: "",
    nome: "",
    sexo: "FEMEA",
    raca: "",
    data_nascimento: "",
    grupo: "BEZERRA",
    mae_id: "",
    pai_touro: "",
    observacoes: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (!payload.numero) throw new Error("Número obrigatório");
      const { data } = await api.post("/animals", payload);
      toast.success(`Animal ${data.numero} cadastrado`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C19]/60 flex items-center justify-center p-4">
      <div className="bg-white border border-[#E2E0D8] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8]">
          <h3 className="font-heading font-bold text-xl tracking-tight">Novo animal</h3>
          <button data-testid="close-animal-form" onClick={onClose} className="p-1">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Número / brinco *">
            <input data-testid="animal-numero" required value={f.numero}
              onChange={(e) => set("numero", e.target.value)} className="input" />
          </Field>
          <Field label="Nome">
            <input data-testid="animal-nome" value={f.nome}
              onChange={(e) => set("nome", e.target.value)} className="input" />
          </Field>
          <Field label="Sexo">
            <select data-testid="animal-sexo" value={f.sexo}
              onChange={(e) => set("sexo", e.target.value)} className="input">
              <option value="FEMEA">Fêmea</option>
              <option value="MACHO">Macho</option>
            </select>
          </Field>
          <Field label="Grupo">
            <select data-testid="animal-grupo" value={f.grupo}
              onChange={(e) => set("grupo", e.target.value)} className="input">
              {GRUPO_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Raça">
            <input value={f.raca} onChange={(e) => set("raca", e.target.value)}
              className="input" placeholder="Ex: Holandesa, Gir, Jersey" />
          </Field>
          <Field label="Data de nascimento">
            <input type="date" value={f.data_nascimento}
              onChange={(e) => set("data_nascimento", e.target.value)} className="input" />
          </Field>
          <Field label="Mãe">
            <select value={f.mae_id} onChange={(e) => set("mae_id", e.target.value)} className="input">
              <option value="">— nenhuma —</option>
              {mothers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.numero} {m.nome ? `- ${m.nome}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Touro (pai)">
            <input value={f.pai_touro} onChange={(e) => set("pai_touro", e.target.value)}
              className="input" placeholder="Código/nome do touro" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Observações">
              <textarea value={f.observacoes} onChange={(e) => set("observacoes", e.target.value)}
                className="input min-h-[80px]" />
            </Field>
          </div>

          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button data-testid="submit-animal" type="submit" disabled={saving} className="btn-primary">
              {saving ? "Salvando…" : "Cadastrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function upload() {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/animals/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} animais importados`);
        onImported();
      } else {
        toast.warning("Nenhum animal criado");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function downloadTemplate() {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/animals-import-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Falha (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modelo_animais.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1C19]/60 flex items-center justify-center p-4">
      <div className="bg-white border border-[#E2E0D8] rounded-sm w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[#E2E0D8]">
          <h3 className="font-heading font-bold text-xl tracking-tight">
            Importar animais (CSV)
          </h3>
          <button onClick={onClose} className="p-1">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-[#4E544B] space-y-2">
            <p>
              Colunas aceitas: <code className="text-[#1A1C19]">numero</code>,{" "}
              <code>nome</code>, <code>sexo</code> (FEMEA/MACHO), <code>raca</code>,{" "}
              <code>data_nascimento</code> (AAAA-MM-DD), <code>grupo</code> (BEZERRA,
              NOVILHA, PRE_PARTO, LACTANTE_PRIMIPARA, LACTANTE_MULTIPARA, SECA),{" "}
              <code>mae</code> (número da mãe já cadastrada), <code>pai_touro</code>,{" "}
              <code>observacoes</code>. Números duplicados são ignorados. Aceita
              vírgula ou ponto e vírgula como separador.
            </p>
            <button
              data-testid="download-template"
              onClick={downloadTemplate}
              className="text-[#2A5A3B] hover:underline text-sm font-bold inline-flex items-center gap-1"
            >
              <FileArrowDown size={14} weight="bold" /> Baixar modelo
            </button>
          </div>

          <input
            data-testid="csv-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border file:border-[#2A5A3B] file:bg-white file:text-[#2A5A3B] file:font-bold file:cursor-pointer"
          />

          {result && (
            <div className="text-sm bg-[#F6F5F2] border border-[#E2E0D8] rounded-sm p-3 space-y-2">
              <div>
                Criados: <b className="text-[#2A5A3B]">{result.created}</b> · Ignorados
                (duplicados): <b>{result.skipped}</b>
              </div>
              {result.warnings?.length > 0 && (
                <details data-testid="import-warnings" className="text-[#8B5A00]">
                  <summary className="cursor-pointer font-bold">
                    Avisos ({result.warnings.length}) — valores ajustados
                  </summary>
                  <ul className="mt-2 space-y-1 max-h-40 overflow-auto text-xs">
                    {result.warnings.slice(0, 30).map((w) => (
                      <li key={`w-${w.linha}-${w.campo}`}>
                        linha {w.linha}: <b>{w.campo}</b> = &quot;{w.valor_original}&quot; → usado <b>{w.usado}</b>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {result.errors?.length > 0 && (
                <div className="text-[#C05746]">
                  Erros ({result.errors.length}):{" "}
                  {result.errors.slice(0, 3).map((e) => (
                    <span key={`${e.linha}-${e.erro}`}>
                      linha {e.linha}: {e.erro};{" "}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-5 border-t border-[#E2E0D8] flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Fechar</button>
          <button
            data-testid="submit-import"
            onClick={upload}
            disabled={busy || !file}
            className="btn-primary inline-flex items-center gap-2"
          >
            <UploadSimple size={16} weight="bold" />
            {busy ? "Importando…" : "Importar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Animals() {
  const { user } = useAuth();
  const [animals, setAnimals] = useState([]);
  const [search, setSearch] = useState("");
  const [grupo, setGrupo] = useState("");
  const [status, setStatus] = useState("ATIVO");
  const [raca, setRaca] = useState("");
  const [delMin, setDelMin] = useState("");
  const [delMax, setDelMax] = useState("");
  const [sortBy, setSortBy] = useState("numero_asc");
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkGrupo, setBulkGrupo] = useState("PRE_PARTO");
  const [savingBulk, setSavingBulk] = useState(false);

  const canCreate = ["admin", "veterinario", "tecnico"].includes(user.role);
  const canBulkGroup = ["admin", "veterinario", "tecnico"].includes(user.role);
  const isAdmin = user.role === "admin";

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (grupo) params.grupo = grupo;
      if (status) params.status = status;
      if (raca) params.raca = raca;
      if (delMin) params.del_min = delMin;
      if (delMax) params.del_max = delMax;
      if (sortBy) params.sort_by = sortBy;
      const { data } = await api.get("/animals", { params });
      setAnimals(data);
      setSelected(new Set());
    } catch (e) {
      setLoadError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, grupo, status, raca, delMin, delMax, sortBy]);

  const mothers = useMemo(() => animals.filter((a) => a.sexo === "FEMEA"), [animals]);

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) =>
      prev.size === animals.length ? new Set() : new Set(animals.map((a) => a.id)),
    );
  }

  async function handleBulkDelete() {
    setDeleting(true);
    try {
      const { data } = await api.post("/animals/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} animais removidos`);
      setConfirmBulk(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleBulkGroup() {
    setSavingBulk(true);
    try {
      const { data } = await api.post("/animals/bulk-group", {
        ids: [...selected],
        grupo: bulkGrupo,
      });
      toast.success(
        `${data.updated} animais movidos para ${GRUPO_LABELS[bulkGrupo] || bulkGrupo}`,
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingBulk(false);
    }
  }

  function clearFilters() {
    setSearch("");
    setGrupo("");
    setStatus("ATIVO");
    setRaca("");
    setDelMin("");
    setDelMax("");
    setSortBy("numero_asc");
  }

  return (
    <div data-testid="animals-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Animais</h1>
          <p className="text-sm text-[#4E544B] mt-1">
            {loading ? "Carregando…" : `${animals.length} animais listados`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <>
              <button
                data-testid="import-csv-btn"
                onClick={() => setShowImport(true)}
                className="btn-secondary inline-flex items-center gap-2"
              >
                <UploadSimple size={16} weight="bold" /> Importar CSV
              </button>
              <button
                data-testid="new-animal-btn"
                onClick={() => setShowForm(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus size={16} weight="bold" /> Novo animal
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4E544B]" />
          <input
            data-testid="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por número ou nome…"
            className="w-full bg-white border border-[#E2E0D8] rounded-sm pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#2A5A3B]"
          />
        </div>
        <select
          data-testid="filter-grupo"
          value={grupo}
          onChange={(e) => setGrupo(e.target.value)}
          className="bg-white border border-[#E2E0D8] rounded-sm px-4 py-2.5 text-sm min-w-[200px]"
        >
          <option value="">Todos os grupos</option>
          {GRUPO_OPTIONS.map((g) => (
            <option key={g.value} value={g.value}>{g.label}</option>
          ))}
        </select>
        <select
          data-testid="sort-by"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-white border border-[#E2E0D8] rounded-sm px-4 py-2.5 text-sm min-w-[200px]"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>Ordenar: {o.label}</option>
          ))}
        </select>
        <button
          data-testid="toggle-filters"
          onClick={() => setShowFilters((s) => !s)}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <Funnel size={16} weight={showFilters ? "fill" : "bold"} />
          Filtros
        </button>
      </div>

      {showFilters && (
        <div
          data-testid="advanced-filters"
          className="bg-white border border-[#E2E0D8] rounded-sm p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end"
        >
          <Field label="Status">
            <select
              data-testid="filter-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="input"
            >
              <option value="">Todos</option>
              <option value="ATIVO">Ativo</option>
              <option value="VENDIDO">Vendido</option>
              <option value="MORTO">Morto</option>
              <option value="DESCARTADO">Descartado</option>
            </select>
          </Field>
          <Field label="Raça">
            <input
              data-testid="filter-raca"
              value={raca}
              onChange={(e) => setRaca(e.target.value)}
              className="input"
              placeholder="Ex: Holandesa"
            />
          </Field>
          <Field label="DEL mín (dias)">
            <input
              data-testid="filter-del-min"
              type="number"
              min={0}
              value={delMin}
              onChange={(e) => setDelMin(e.target.value)}
              className="input"
              placeholder="Ex: 0"
            />
          </Field>
          <Field label="DEL máx (dias)">
            <input
              data-testid="filter-del-max"
              type="number"
              min={0}
              value={delMax}
              onChange={(e) => setDelMax(e.target.value)}
              className="input"
              placeholder="Ex: 305"
            />
          </Field>
          <button
            data-testid="clear-filters"
            onClick={clearFilters}
            className="btn-secondary inline-flex items-center justify-center gap-2 h-[42px]"
          >
            <ArrowsClockwise size={16} weight="bold" /> Limpar
          </button>
        </div>
      )}

      {loadError && <LoadError error={loadError} onRetry={load} />}

      {selected.size > 0 && (
        <div
          data-testid="bulk-toolbar"
          className="bg-[#FFF6E5] border border-[#D4A017]/30 rounded-sm p-3 flex flex-col md:flex-row md:items-center gap-3 md:justify-between"
        >
          <span className="text-sm font-bold text-[#8B5A00]">
            {selected.size} animais selecionados
          </span>
          <div className="flex flex-wrap gap-2 items-center">
            {canBulkGroup && (
              <>
                <select
                  data-testid="bulk-grupo-select"
                  value={bulkGrupo}
                  onChange={(e) => setBulkGrupo(e.target.value)}
                  className="bg-white border border-[#E2E0D8] rounded-sm px-3 py-2 text-sm"
                >
                  {GRUPO_OPTIONS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
                <button
                  data-testid="bulk-move-group-btn"
                  onClick={handleBulkGroup}
                  disabled={savingBulk}
                  className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
                >
                  {savingBulk ? "Movendo…" : "Mover para grupo"}
                </button>
              </>
            )}
            {isAdmin && (
              <button
                data-testid="bulk-delete-btn"
                onClick={() => setConfirmBulk(true)}
                className="bg-[#C05746] hover:bg-[#A04030] text-white font-bold text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2"
              >
                <Trash size={16} weight="bold" /> Excluir selecionados
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E2E0D8] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F5F2] border-b border-[#E2E0D8]">
              <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#4E544B]">
                {canBulkGroup && (
                  <th className="px-4 py-3 w-10">
                    <input
                      data-testid="select-all"
                      type="checkbox"
                      className="accent-[#2A5A3B] w-4 h-4"
                      checked={animals.length > 0 && selected.size === animals.length}
                      onChange={toggleAll}
                      aria-label="Selecionar todos"
                    />
                  </th>
                )}
                <th className="px-4 py-3">Número</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Grupo</th>
                <th className="px-4 py-3">Raça</th>
                <th className="px-4 py-3">Sexo</th>
                <th className="px-4 py-3">Nascimento</th>
                <th className="px-4 py-3">DEL</th>
                <th className="px-4 py-3">Prod. 7d</th>
                <th className="px-4 py-3">GMD</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody data-testid="animals-table">
              {animals.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={canBulkGroup ? 11 : 10}
                    className="text-center py-10 text-[#4E544B]"
                  >
                    Nenhum animal encontrado.
                  </td>
                </tr>
              )}
              {animals.map((a) => (
                <tr
                  key={a.id}
                  className="border-t border-[#E2E0D8] hover:bg-[#F6F5F2] transition-colors"
                >
                  {canBulkGroup && (
                    <td className="px-4 py-3">
                      <input
                        data-testid={`select-${a.numero}`}
                        type="checkbox"
                        className="accent-[#2A5A3B] w-4 h-4"
                        checked={selected.has(a.id)}
                        onChange={() => toggleSelect(a.id)}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-bold">
                    <Link
                      to={`/animais/${a.id}`}
                      data-testid={`animal-row-${a.numero}`}
                      className="text-[#2A5A3B] hover:underline"
                    >
                      {a.numero}
                    </Link>
                  </td>
                  <td className="px-4 py-3">{a.nome || "-"}</td>
                  <td className="px-4 py-3">{GRUPO_LABELS[a.grupo] || a.grupo}</td>
                  <td className="px-4 py-3">{a.raca || "-"}</td>
                  <td className="px-4 py-3">{a.sexo === "FEMEA" ? "Fêmea" : "Macho"}</td>
                  <td className="px-4 py-3">{formatDate(a.data_nascimento)}</td>
                  <td className="px-4 py-3">
                    {a.del !== null && a.del !== undefined ? `${a.del} d` : "-"}
                  </td>
                  <td className="px-4 py-3">
                    {a.producao_7d && a.producao_7d > 0
                      ? `${Number(a.producao_7d).toFixed(1)} L`
                      : "-"}
                  </td>
                  <td className="px-4 py-3">
                    {a.gmd_kg_dia !== null && a.gmd_kg_dia !== undefined
                      ? `${(a.gmd_kg_dia * 1000).toFixed(0)} g`
                      : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${
                        a.status === "ATIVO"
                          ? "bg-[#E9F5EC] text-[#2A5A3B] border-[#2A5A3B]/20"
                          : "bg-[#FDEDED] text-[#C05746] border-[#C05746]/20"
                      }`}
                    >
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <AnimalForm
          mothers={mothers}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={load}
        />
      )}

      {confirmBulk && (
        <div className="fixed inset-0 z-50 bg-[#1A1C19]/60 flex items-center justify-center p-4">
          <div
            data-testid="bulk-delete-modal"
            className="bg-white border border-[#E2E0D8] rounded-sm w-full max-w-md"
          >
            <div className="flex items-center gap-2 p-5 border-b border-[#E2E0D8]">
              <Warning size={22} weight="duotone" color="#C05746" />
              <h3 className="font-heading font-bold text-xl tracking-tight">
                Excluir {selected.size} animais
              </h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p>
                Esta ação vai remover permanentemente {selected.size} animais e{" "}
                <b>todos os registros relacionados</b> (cios, inseminações, partos,
                tratamentos, pesagens e produção).
              </p>
              <p className="text-[#4E544B] text-xs">
                Para preservar o histórico, prefira alterar o status para Vendido,
                Morto ou Descartado.
              </p>
            </div>
            <div className="p-5 border-t border-[#E2E0D8] flex justify-end gap-3">
              <button
                onClick={() => setConfirmBulk(false)}
                disabled={deleting}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-bulk-delete"
                onClick={handleBulkDelete}
                disabled={deleting}
                className="bg-[#C05746] hover:bg-[#A04030] disabled:opacity-60 text-white font-bold px-5 py-2 rounded-sm text-sm inline-flex items-center gap-2"
              >
                <Trash size={16} weight="bold" />
                {deleting ? "Excluindo…" : "Excluir permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
